<!--

 -->

<template>
  <div>
    <div class="gsl-hrm-background-container">
      <slot
        :filters="filters"
        :rules="rules"
        :filter-items="filterItems"
        :on-reset="onReset"
        :on-change-view="onChangeView"
        :is-mobile="isMobile"
      ></slot>
      <br />
      <div
        v-show="filters.currentView === 'list'"
        class="gsl-hrm-paper-container"
      >
        <leave-list-table-header
          :selected="checkedItems.length"
          :total="total"
          :loading="isLoading"
          :bulk-actions="leaveBulkActions"
          @on-action-click="onLeaveActionBulk"
        >
        </leave-list-table-header>
        <div class="gsl-hrm-container">
          <oxd-card-table
            v-model:selected="checkedItems"
            :headers="headers"
            :items="items?.data"
            :selectable="true"
            :clickable="false"
            :loading="isLoading"
            class="gsl-hrm-leave-list"
            row-decorator="oxd-table-decorator-card"
          />
        </div>
        <div class="gsl-hrm-bottom-container">
          <oxd-pagination
            v-if="showPaginator"
            v-model:current="currentPage"
            :length="pages"
          />
        </div>
      </div>
      <div
        v-show="filters.currentView === 'calendar'"
        class="gsl-hrm-paper-container"
        style="overflow: hidden"
      >
        <div class="gsl-hrm-calendar-wrapper">
          <calendar-view
            :show-date="showDate"
            :items="eventItems?.data"
            display-period-uom="month"
            :weekday-name-format="isMobile ? 'short' : 'long'"
            item-content-height="1.5rem"
            class="theme-default holiday-us-traditional holiday-us-official"
            :starting-day-of-week="1"
            @click-item="onClickCalendarItem"
          >
            <template #header="{headerProps}">
              <div class="calendar-header">
                <div class="calendar-period">
                  <oxd-icon-button
                    name="caret-left-fill"
                    class="calendar-nav-btn"
                    @click.prevent="setShowDate(headerProps.previousPeriod)"
                  />
                  <slot name="label">
                    <span class="calendar-label">
                      {{ headerProps.periodLabel }}
                    </span></slot
                  >
                  <oxd-icon-button
                    name="caret-right-fill"
                    class="calendar-nav-btn"
                    @click.prevent="setShowDate(headerProps.nextPeriod)"
                  />
                </div>
              </div>
            </template>
          </calendar-view>
        </div>
        <div class="gsl-hrm-footer-guide-container">
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="dot pending-leave"></span>
            {{ 'Taken/Scheduled' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="dot approve-leave"></span>
            {{ 'Pending' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="dot cancelled-leave"></span>
            {{ 'Cancelled' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="dot rejected-leave"></span>
            {{ 'Rejected' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="dot weekend-leave"></span>
            {{ 'Weekend' }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-footer-link-title">
            <span class="dot holiday-leave"></span>
            {{ 'Holiday' }}
          </oxd-text>
        </div>
      </div>
    </div>
    <leave-comment-modal
      v-if="showCommentModal"
      :id="commentModalState"
      @close="onCommentModalClose"
    >
    </leave-comment-modal>
    <approval-confirmation
      ref="approvalDialog"
      :title="$t('leave.leave_approval_confirmation')"
      :subtitle="$t('leave.leave_approval_confirmation_subtitle')"
      :content="listOfSubordinatesState"
      :cancel-label="$t('general.no_cancel')"
      :confirm-label="$t('leave.yes_approve')"
    ></approval-confirmation>
    <leave-bulk-action-modal ref="bulkActionModal" :data="bulkActionModalState">
    </leave-bulk-action-modal>
  </div>
</template>

<script>
import {
  required,
  validSelection,
  validDateFormat,
  endDateShouldBeAfterStartDate,
} from '@/core/util/validation/rules';
import {
  startOfMonth,
  endOfMonth,
  formatDate,
  parseDate,
  eachDayOfInterval,
} from '@/core/util/helper/datefns';
import {computed, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {truncate} from '@ohrm/core/util/helper/truncate';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useLeaveActions from '@/orangehrmLeavePlugin/util/composable/useLeaveActions';
import LeaveCommentsModal from '@/orangehrmLeavePlugin/components/LeaveCommentsModal';
import LeaveBulkActionModal from '@/orangehrmLeavePlugin/components/LeaveBulkActionModal';
import LeaveListTableHeader from '@/orangehrmLeavePlugin/components/LeaveListTableHeader';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';
import ConfirmationDialog from '@/orangehrmLeavePlugin/components/LeaveApprovalConfirmationModel';
// Calendar view
import {CalendarView} from 'vue-simple-calendar';
import 'vue-simple-calendar/dist/vue-simple-calendar.css';
import 'vue-simple-calendar/dist/css/default.css';
import 'vue-simple-calendar/dist/css/holidays-us.css';
import {useResponsive} from '@ohrm/oxd';
import {toRefs} from 'vue';

const defaultFilters = {
  employee: null,
  fromDate: null,
  toDate: null,
  statuses: [],
  subunit: null,
  includePastEmps: false,
  leaveType: null,
  currentView: ref('list'),
};

export default {
  name: 'LeaveListTable',

  components: {
    'leave-list-table-header': LeaveListTableHeader,
    'leave-comment-modal': LeaveCommentsModal,
    'leave-bulk-action-modal': LeaveBulkActionModal,
    'approval-confirmation': ConfirmationDialog,
    'calendar-view': CalendarView,
  },

  props: {
    myLeaveList: {
      type: Boolean,
      default: false,
    },
    leaveStatuses: {
      type: Array,
      default: () => [],
    },
    employee: {
      type: Object,
      required: false,
      default: () => null,
    },
    leaveType: {
      type: Object,
      required: false,
      default: () => null,
    },
    fromDate: {
      type: String,
      required: false,
      default: null,
    },
    toDate: {
      type: String,
      required: false,
      default: null,
    },
    leaveStatus: {
      type: Object,
      required: false,
      default: () => null,
    },
  },

  setup(props) {
    const filters = ref({
      ...defaultFilters,
      ...(props.leaveType && {leaveType: props.leaveType}),
      ...(props.fromDate && {fromDate: props.fromDate}),
      ...(props.toDate && {toDate: props.toDate}),
      ...(props.leaveStatus && {statuses: [props.leaveStatus]}),
      ...(props.employee && {
        employee: {
          id: props.employee.empNumber,
          label: `${props.employee.firstName} ${props.employee.middleName} ${props.employee.lastName}`,
          isPastEmployee: props.employee.terminationId,
        },
      }),
    });
    const checkedItems = ref([]);
    const {$t} = usei18n();
    const {locale} = useLocale();
    const {jsDateFormat, userDateFormat} = useDateFormat();
    const responsiveState = useResponsive();

    const rules = {
      fromDate: [required, validDateFormat(userDateFormat)],
      toDate: [
        required,
        validDateFormat(userDateFormat),
        endDateShouldBeAfterStartDate(
          () => filters.value.fromDate,
          $t('general.to_date_should_be_after_from_date'),
          {allowSameDate: true},
        ),
      ],
      statuses: [required],
      employee: [validSelection],
    };

    const serializedFilters = computed(() => {
      const statuses = Array.isArray(filters.value.statuses)
        ? filters.value.statuses
        : [];

      return {
        empNumber: filters.value.employee?.id,
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
        subunitId: filters.value.subunit?.id,
        includeEmployees: filters.value.includePastEmps
          ? 'currentAndPast'
          : 'onlyCurrent',
        statuses: statuses.map((item) => item.id),
        leaveTypeId: filters.value.leaveType?.id,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/leave/${
        props.myLeaveList ? 'leave-requests' : 'employees/leave-requests'
      }`,
    );

    const httpCalendar = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/leave/${
        props.myLeaveList
          ? 'leave-requests-calendar'
          : 'employees/leave-requests-calendar'
      }`,
    );

    const leaveListNormalizer = (data) => {
      return data.map((item) => {
        let leaveDatePeriod,
          leaveStatuses,
          leaveBalances = '';
        const duration = item.dates.durationType?.type;

        if (item.dates.fromDate) {
          leaveDatePeriod = formatDate(
            parseDate(item.dates.fromDate),
            jsDateFormat,
            {locale},
          );
        }
        if (item.dates.toDate) {
          leaveDatePeriod += ` to ${formatDate(
            parseDate(item.dates.toDate),
            jsDateFormat,
            {locale},
          )}`;
        }
        if (item.dates.startTime && item.dates.endTime) {
          leaveDatePeriod += ` (${item.dates.startTime} - ${item.dates.endTime})`;
        }
        if (
          duration === 'half_day_morning' ||
          duration === 'half_day_afternoon'
        ) {
          leaveDatePeriod += ` ${$t('leave.half_day')}`;
        }
        if (Array.isArray(item.leaveBreakdown)) {
          leaveStatuses = item.leaveBreakdown
            .map(
              (status) =>
                `${status.name} (${parseFloat(status.lengthDays).toFixed(2)})`,
            )
            .join(', ');
        }
        if (Array.isArray(item.leaveBalances)) {
          if (item.leaveBalances.length > 1) {
            leaveBalances = item.leaveBalances
              .map(({period, balance}) => {
                const _balance = parseFloat(balance.balance).toFixed(2);
                const startDate = formatDate(
                  parseDate(period.startDate),
                  jsDateFormat,
                  {locale},
                );
                const endDate = formatDate(
                  parseDate(period.endDate),
                  jsDateFormat,
                  {locale},
                );
                return `${_balance} (${startDate} - ${endDate})`;
              })
              .join(', ');
          } else {
            const balance = item.leaveBalances[0]?.balance.balance;
            leaveBalances = balance ? parseFloat(balance).toFixed(2) : '0.00';
          }
        }

        const empName = `${item.employee?.firstName} ${item.employee?.middleName} ${item.employee?.lastName}`;
        const leaveTypeName = item.leaveType?.name;

        if (item.employee?.terminationId) {
          empName + ` (${$t('general.past_employee')})`;
        }
        if (item.leaveType?.deleted) {
          leaveTypeName + ` (${$t('general.deleted')})`;
        }
        return {
          id: item.id,
          empNumber: item.employee?.empNumber,
          date: leaveDatePeriod,
          employeeName: empName,
          leaveType: leaveTypeName,
          leaveBalance: leaveBalances,
          days: parseFloat(item.noOfDays).toFixed(2),
          status: leaveStatuses,
          comment: truncate(item.lastComment?.comment),
          actions: item.allowedActions,
        };
      });
    };

    const {
      leaveActions,
      processLeaveRequestAction,
      processLeaveRequestBulkAction,
    } = useLeaveActions(http);

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: leaveListNormalizer,
    });

    const leaveBulkActions = computed(() => {
      if (checkedItems.value.length > 0 && response.value.data) {
        const allActions = checkedItems.value.map((item) => {
          return response.value.data[item].actions;
        });
        return {
          APPROVE: allActions.reduce(
            (approvable, actions) =>
              approvable && actions.find((i) => i.action === 'APPROVE'),
            true,
          ),
          REJECT: allActions.reduce(
            (rejectable, actions) =>
              rejectable && actions.find((i) => i.action === 'REJECT'),
            true,
          ),
          CANCEL: allActions.reduce(
            (cancelable, actions) =>
              cancelable && actions.find((i) => i.action === 'CANCEL'),
            true,
          ),
        };
      }
      return null;
    });

    const leaveCalendarNormalizer = (data) => {
      const getClass = (status) =>
        ({
          'Pending Approval': 'pending-leave',
          Taken: 'approve-leave',
          Scheduled: 'approve-leave',
          Cancelled: 'cancelled-leave',
          Rejected: 'rejected-leave',
          Weekend: 'weekend-leave',
          Holiday: 'holiday-leave',
        }[status] || '');

      return data.map((item) => {
        const empName = `${item.employee?.firstName || ''} ${
          item.employee?.middleName || ''
        } ${item.employee?.lastName || ''}${
          item.employee?.terminationId
            ? ` (${$t('general.past_employee')})`
            : ''
        }`;

        const fromDate = item.dates?.fromDate
          ? parseDate(item.dates.fromDate)
          : null;
        const toDate = item.dates?.toDate
          ? parseDate(item.dates.toDate)
          : fromDate;

        const days = eachDayOfInterval({start: fromDate, end: toDate});
        const dayStatuses = days.map((day) => {
          const dayStr = formatDate(day, 'yyyy-MM-dd');
          const statusObj = item.leaveBreakdown?.find((s) =>
            s.date?.date?.startsWith(dayStr),
          );
          return statusObj?.name ?? '';
        });

        const uniqueStatuses = Array.from(new Set(dayStatuses));
        const isSingleStatus = uniqueStatuses.length === 1;

        const title = `<div class="web-title"><b>#${
          item.id
        }</b></div> ${empName}${
          !isSingleStatus
            ? ' <div class="web-title">' +
              days
                .map(
                  (day, i) =>
                    `<span class="dot ${getClass(dayStatuses[i])}">${formatDate(
                      day,
                      'dd',
                    )}</span>`,
                )
                .join(' ') +
              '</div>'
            : ''
        }`;
        const datePeriod =
          fromDate.getTime() === toDate.getTime()
            ? formatDate(fromDate, jsDateFormat, {locale})
            : `${formatDate(fromDate, jsDateFormat, {locale})} - ${formatDate(
                toDate,
                jsDateFormat,
                {locale},
              )}`;

        const leaveTypeName = item.leaveType?.name;
        return {
          id: `${item.id}`,
          startDate: formatDate(fromDate, jsDateFormat, {locale}),
          endDate: formatDate(toDate, jsDateFormat, {locale}),
          title,
          tooltip: `Name: ${empName}\nType: ${leaveTypeName}\nDate: ${datePeriod}`,
          classes: [getClass(isSingleStatus ? uniqueStatuses[0] : '')],
        };
      });
    };

    const {execQuery: eventExecQuery, response: eventResponse} = usePaginate(
      httpCalendar,
      {
        query: serializedFilters,
        normalizer: leaveCalendarNormalizer,
        pageSize: 0,
      },
    );
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      rules,
      filters,
      checkedItems,
      leaveActions,
      leaveBulkActions,
      processLeaveRequestAction,
      processLeaveRequestBulkAction,
      eventItems: eventResponse,
      eventExecQuery,
      ...toRefs(responsiveState),
    };
  },
  data() {
    const today = new Date();

    return {
      headers: [
        {name: 'date', title: this.$t('general.date'), style: {flex: 1}},
        {
          name: 'employeeName',
          title: this.$t('general.employee_name'),
          style: {flex: 1},
        },
        {
          name: 'leaveType',
          title: this.$t('leave.leave_type'),
          style: {flex: 1},
        },
        {
          name: 'leaveBalance',
          title: this.$t('leave.leave_balance_days'),
          style: {flex: 1},
        },
        {
          name: 'days',
          title: this.$t('leave.number_of_days'),
          style: {flex: 1},
        },
        {name: 'status', title: this.$t('general.status'), style: {flex: 1}},
        {
          name: 'comment',
          title: this.$t('general.comments'),
          style: {flex: '5%'},
        },
        {
          name: 'action',
          slot: 'footer',
          title: this.$t('general.actions'),
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.cellRenderer,
          style: {
            flex: this.myLeaveList ? '10%' : '20%',
          },
        },
      ],
      showCommentModal: false,
      commentModalState: null,
      bulkActionModalState: {},
      listOfSubordinatesState: {},
      showDate: today,
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 800;
    },
  },
  beforeMount() {
    this.isLoading = true;
    if (this.filters.statuses.length === 0) {
      this.filters.statuses = this.myLeaveList
        ? this.leaveStatuses
        : this.leaveStatuses.filter((status) => status.id === 1);
    }
    this.http
      .request({method: 'GET', url: '/api/v2/leave/leave-periods'})
      .then((response) => {
        const {data, meta} = response.data;
        if (meta.leavePeriodDefined) {
          this.filters.fromDate =
            this.filters.fromDate ?? meta?.currentLeavePeriod.startDate;
          this.filters.toDate =
            this.filters.toDate ?? meta?.currentLeavePeriod.endDate;
        } else {
          this.filters.fromDate = this.filters.fromDate ?? data[0]?.startDate;
          this.filters.toDate = this.filters.toDate ?? data[0]?.endDate;
        }
      })
      .finally(() => {
        this.isLoading = false;
        Object.assign(defaultFilters, this.filters);
      });
  },

  methods: {
    cellRenderer(...[, , , row]) {
      const cellConfig = {};
      const {approve, reject, cancel, more} = this.leaveActions;
      const dropdownActions = [
        {label: this.$t('general.add_comment'), context: 'add_comment'},
        {label: this.$t('leave.view_leave_details'), context: 'leave_details'},
        {label: this.$t('leave.view_pim_info'), context: 'pim_details'},
      ];

      row.actions.map((item) => {
        if (item.action === 'APPROVE') {
          approve.props.label = this.$t('general.approve');
          approve.props.onClick = () => this.onLeaveAction(row.id, 'APPROVE');
          cellConfig.approve = approve;
        }
        if (item.action === 'REJECT') {
          reject.props.label = this.$t('general.reject');
          reject.props.onClick = () => this.onLeaveAction(row.id, 'REJECT');
          cellConfig.reject = reject;
        }
        if (item.action === 'CANCEL') {
          if (this.myLeaveList) {
            cancel.props.label = this.$t('general.cancel');
            cancel.props.onClick = () => this.onLeaveAction(row.id, 'CANCEL');
            cellConfig.reject = cancel;
          } else {
            dropdownActions.push({
              label: this.$t('leave.cancel_leave'),
              context: 'cancel_leave',
            });
          }
        }
      });

      more.props.options = dropdownActions;
      more.props.onClick = ($event) => this.onLeaveDropdownAction($event, row);
      cellConfig.more = more;

      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onLeaveDropdownAction(event, item) {
      switch (event.context) {
        case 'add_comment':
          this.commentModalState = item.id;
          this.showCommentModal = true;
          break;
        case 'cancel_leave':
          this.onLeaveAction(item.id, 'CANCEL');
          break;
        case 'pim_details':
          navigate('/pim/viewPersonalDetails/empNumber/{id}', {
            id: item.empNumber,
          });
          break;
        default:
          navigate(
            '/leave/viewLeaveRequest/{id}',
            {id: item.id},
            this.myLeaveList && {mode: 'my-leave'},
          );
      }
    },
    async onLeaveAction(id, actionType) {
      this.isLoading = true;

      if (actionType === 'APPROVE') {
        const response = await this.http.request({
          method: 'GET',
          url: `/api/v2/leave/leave-employee/${id}/validation/subordinate`,
        });

        const {data} = response.data;
        if (data.valid) {
          this.listOfSubordinatesState = data?.list || {};
          const confirmation = await this.$refs.approvalDialog.showDialog();

          if (confirmation !== 'ok') {
            this.isLoading = false;
            return;
          }
        }
      }

      this.processLeaveRequestAction(id, actionType)
        .then(() => {
          this.$toast.updateSuccess();
        })
        .finally(this.resetDataTable);
    },
    async onLeaveActionBulk(actionType) {
      this.isLoading = true;
      this.bulkActionModalState = {
        count: this.checkedItems.length,
        action: actionType,
      };

      const ids = this.checkedItems.map((index) => {
        return this.items.data[index].id;
      });

      let combinedSubordinates = [];

      if (actionType === 'APPROVE') {
        const responses = await Promise.all(
          ids.map((id) =>
            this.http.request({
              method: 'GET',
              url: `/api/v2/leave/leave-employee/${id}/validation/subordinate`,
            }),
          ),
        );
        combinedSubordinates = [
          ...new Set(
            responses.flatMap((res) => res.data.data.list), // extract list from each response
          ),
        ];
        this.bulkActionModalState = {
          ...this.bulkActionModalState,
          content: combinedSubordinates,
        };
      }

      const confirmation = await this.$refs.bulkActionModal.showDialog();

      if (confirmation !== 'ok') {
        this.isLoading = false;
        return;
      }

      this.processLeaveRequestBulkAction(ids, actionType)
        .then((response) => {
          const {data} = response.data;
          if (Array.isArray(data))
            this.$toast.success({
              title: this.$t('general.success'),
              message: this.$t('leave.leave_requests_action', {
                action: actionType,
                count: data.length,
              }),
            });
        })
        .finally(() => {
          this.bulkActionModalState = {};
          this.resetDataTable();
        });
    },
    onCommentModalClose() {
      this.commentModalState = null;
      this.showCommentModal = false;
      this.resetDataTable();
    },
    async resetDataTable() {
      this.checkedItems = [];
      if (this.filters?.currentView === 'calendar') {
        await this.eventExecQuery();
      } else {
        await this.execQuery();
      }
    },
    async filterItems() {
      if (this.filters?.currentView === 'calendar') {
        const today = new Date();

        let initialShowDate = today;

        if (this.filters?.fromDate && this.filters?.toDate) {
          const from = parseDate(this.filters.fromDate);
          const to = parseDate(this.filters.toDate);

          // Check if today's month is between fromDate and toDate
          const isCurrentMonthInRange =
            today >= startOfMonth(from) && today <= endOfMonth(to);

          initialShowDate = isCurrentMonthInRange ? today : from;
        }
        this.setShowDate(initialShowDate);
        await this.eventExecQuery();
      } else {
        await this.execQuery();
      }
    },
    onReset() {
      this.filters = {...defaultFilters};
      this.resetDataTable();
    },
    async setShowDate(d) {
      this.showDate = d;

      if (this.filters.currentView === 'calendar') {
        this.filters.fromDate = formatDate(startOfMonth(d), 'yyyy-MM-dd');
        this.filters.toDate = formatDate(endOfMonth(d), 'yyyy-MM-dd');

        // reload data with new filters
        await this.eventExecQuery();
      }
    },
    async onChangeView(view) {
      this.filters.currentView = view;
      if (view === 'calendar') {
        const today = new Date();

        let initialShowDate = today;

        if (this.filters?.fromDate && this.filters?.toDate) {
          const from = parseDate(this.filters.fromDate);
          const to = parseDate(this.filters.toDate);

          // Check if today's month is between fromDate and toDate
          const isCurrentMonthInRange =
            today >= startOfMonth(from) && today <= endOfMonth(to);

          initialShowDate = isCurrentMonthInRange ? today : from;
        }
        this.setShowDate(initialShowDate);
        await this.eventExecQuery();
      } else {
        await this.execQuery();
      }
    },
    onClickCalendarItem(item) {
      navigate(
        '/leave/viewLeaveRequest/{id}',
        {id: item.id},
        this.myLeaveList && {mode: 'my-leave'},
      );
    },
  },
};
</script>

<style src="./leave-list-table.scss" lang="scss" scoped></style>
